import { Button, Group, Paper, Stack, Text, TextInput, Tooltip } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { ResourceType } from '@/services/api/api.types'
import { getResourceIcon } from '@/utils/contentTypes'

type UploadedFilePreviewProps = {
  fileName: string
  type: ResourceType
  resourceKey?: string
  resourceData?: string
  onFileNameChange?: (newFileName: string) => void
}

export function UploadedFilePreview({
  fileName,
  type,
  resourceKey,
  resourceData,
  onFileNameChange,
}: UploadedFilePreviewProps) {
  const { configStore, api } = useGlobalState()
  const [isOpening, setIsOpening] = useState(false)

  // Split file name into base name and extension
  const lastDotIndex = fileName.lastIndexOf('.')
  const baseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName
  const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1).toUpperCase() : ''

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(baseName)

  const Icon = getResourceIcon(type)

  const isValid = editValue.trim().length > 0
  const canPreview = !!resourceData || !!resourceKey

  const handleSave = () => {
    if (isValid && onFileNameChange) {
      onFileNameChange(editValue.trim() + '.' + extension.toLowerCase())
    } else {
      // Reset to original if invalid
      setEditValue(baseName)
    }
    setIsEditing(false)
  }

  const handleStartEdit = () => {
    if (!onFileNameChange) return
    setEditValue(baseName)
    setIsEditing(true)
  }

  const handleOpenPreview = async () => {
    if (isOpening) return

    // If we have resourceData, use it directly
    if (resourceData) {
      window.open(`${configStore.serverBaseUrl}/files/${resourceData}`, '_blank')
      return
    }

    // Otherwise, do the two-step fetch using resourceKey
    if (!resourceKey) return

    setIsOpening(true)
    try {
      const tempFileId = await api.download({ file_path: resourceKey })
      if (tempFileId && typeof tempFileId === 'string') {
        window.open(`${configStore.serverBaseUrl}/files/${tempFileId}`, '_blank')
      }
    } catch (error) {
      console.error('Failed to get file preview URL:', error)
    } finally {
      setIsOpening(false)
    }
  }

  return (
    <Paper withBorder p="sm" radius="sm" mt={0} bg="gray.1">
      <Group gap="sm" align="center">
        {/* getResourceIcon picks from a fixed set of module-level Tabler icons rather than
            defining a component, so the identity is stable and there is no state to reset. */}
        {/* eslint-disable-next-line react-hooks/static-components */}
        <Icon size={36} stroke={1.5} />
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <TextInput
              size="sm"
              value={editValue}
              onChange={e => setEditValue(e.currentTarget.value)}
              onBlur={handleSave}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave()
                if (e.key === 'Escape') {
                  setEditValue(baseName)
                  setIsEditing(false)
                }
              }}
              error={!isValid}
              autoFocus
            />
          ) : (
            <Text
              size="md"
              fw={500}
              onClick={handleStartEdit}
              style={{
                cursor: onFileNameChange ? 'pointer' : 'default',
                textDecoration: onFileNameChange ? 'underline' : 'none',
                textDecorationStyle: 'solid',
                textUnderlineOffset: 3,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}>
              {baseName}
            </Text>
          )}
          {extension && (
            <Text size="xs" c="dimmed" pt="xs">
              {extension}
            </Text>
          )}
        </Stack>
        {canPreview && (
          <Tooltip label="Open in new tab">
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              rightSection={<IconExternalLink size={16} />}
              onClick={handleOpenPreview}
              loading={isOpening}>
              Preview
            </Button>
          </Tooltip>
        )}
      </Group>
    </Paper>
  )
}
